import { phoneHomeRows, type HeroSlide, type HomeRow } from '@player/domain';
import { FONT_STACK, colors } from '../theme.js';
import { useTranslator } from '../i18n/I18nProvider.js';
import { eventLine } from '../screens/HomeScreen.js';

/**
 * La Home sul telefono: hero che si scorre col pollice, poi le fasce.
 *
 * ## L'hero **non ruota**, e non è una funzione in meno
 *
 * Sul televisore le tre slide ruotano da sole, perché lì non c'è un modo comodo di scorrerle: il
 * telecomando può, ma nessuno lo fa. Sul telefono il gesto c'è, è naturale, ed è il pollice — e una
 * rotazione automatica **glielo ruba**: si comincia a leggere il secondo, la pagina scorre da sola,
 * e si perde il punto. Peggio ancora quando si sta per toccare un pulsante e sotto il dito cambia
 * quel che si stava per aprire.
 *
 * Quindi tutte e tre sono lì, affiancate, e si scorre (`docs/04-SCHERMATE-MOBILE.md` §3).
 *
 * ## `scroll-snap`, perché mezza scheda non è una scheda
 *
 * Senza aggancio, uno scorrimento lascia due mezzi riquadri ai lati dello schermo e il pollice deve
 * aggiustare. Con `snap-align: center` ogni gesto approda su una slide intera, e la successiva si
 * intravede quel tanto che basta a far capire che ce n'è un'altra.
 *
 * ## Le categorie non ci sono
 *
 * Lo decide `phoneHomeRows`, non questa schermata: è una regola, e nel dominio si prova. Qui si
 * disegna quel che arriva.
 */
export function PhoneHomeScreen({
  heroSlides,
  rows,
  onOpenHero,
  onOpenRowItem,
}: {
  readonly heroSlides: ReadonlyArray<HeroSlide>;
  readonly rows: ReadonlyArray<HomeRow>;
  /** `which` distingue i due pulsanti della slide: l'azione principale e quella secondaria. */
  readonly onOpenHero: (slideId: string, which: 'primary' | 'secondary') => void;
  readonly onOpenRowItem: (row: HomeRow, itemId: string) => void;
}) {
  const translator = useTranslator();
  const fasce = phoneHomeRows(rows);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 24, padding: '8px 0 28px' }}>
      {heroSlides.length > 0 && (
        <div
          data-phone-hero="1"
          style={{
            display: 'flex',
            gap: 12,
            overflowX: 'auto',
            scrollSnapType: 'x mandatory',
            scrollbarWidth: 'none',
            padding: '0 20px',
          }}
        >
          {heroSlides.map((slide) => (
            <article
              key={slide.id}
              style={{
                flex: 'none',
                width: 326,
                scrollSnapAlign: 'center',
                borderRadius: 18,
                overflow: 'hidden',
                background: colors.surface,
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              <div
                aria-hidden
                style={{
                  height: 184,
                  background: colors.surfaceProgram,
                  backgroundImage: slide.image !== null ? `url(${slide.image})` : undefined,
                  backgroundSize: 'cover',
                  backgroundPosition: 'center',
                }}
              />
              <div style={{ padding: '14px 16px 16px', display: 'flex', flexDirection: 'column', gap: 6 }}>
                <span
                  style={{
                    font: `700 11px ${FONT_STACK}`,
                    color: colors.accent,
                    letterSpacing: '.1em',
                    textTransform: 'uppercase',
                  }}
                >
                  {slide.kicker}
                </span>
                <h2 style={{ margin: 0, font: `800 24px ${FONT_STACK}`, letterSpacing: '-.02em' }}>
                  {slide.title}
                </h2>
                {/*
                  Un evento compone la sua riga dai pezzi tradotti — «Stasera alle 20:45 · Sul canale
                  Arena Calcio» — e la compone **la stessa funzione del televisore**: riscriverla qui
                  vorrebbe dire due frasi da tenere d'accordo in sette lingue, e due apparecchi che
                  annunciano lo stesso evento con parole diverse.
                */}
                <span style={{ font: `500 12px ${FONT_STACK}`, color: colors.textDim }}>
                  {slide.when !== null
                    ? eventLine(translator, slide.when, slide.channel)
                    : slide.meta}
                </span>
                {/*
                  Le parole dei due pulsanti **le porta la slide**, non questa schermata.

                  Dicono cose diverse secondo cosa si sta annunciando — «Guarda», «Riprendi», «Vai al
                  canale», «Mettimi un promemoria» — e la scelta la fa `heroSlides` nel dominio, dove
                  si prova. Avevo scritto due chiavi inventate, `hero.primary` e `hero.secondary`:
                  non esistono in nessuna delle sette lingue, e l'ha trovato `messages.test.ts`.

                  Il secondo pulsante può non esserci: una slide con una sola azione non deve
                  mostrarne uno vuoto.
                */}
                <div style={{ display: 'flex', gap: 8, paddingTop: 8 }}>
                  <button
                    type="button"
                    onClick={() => onOpenHero(slide.id, 'primary')}
                    style={{
                      flex: 1,
                      height: 44,
                      border: 0,
                      borderRadius: 12,
                      background: colors.text,
                      color: colors.background,
                      font: `700 14px ${FONT_STACK}`,
                      cursor: 'pointer',
                    }}
                  >
                    {translator.t(slide.primary.labelKey)}
                  </button>
                  {slide.secondary !== null && (
                    <button
                      type="button"
                      onClick={() => onOpenHero(slide.id, 'secondary')}
                      style={{
                        flex: 1,
                        height: 44,
                        border: 0,
                        borderRadius: 12,
                        background: colors.surfaceButton,
                        color: colors.text,
                        font: `600 14px ${FONT_STACK}`,
                        cursor: 'pointer',
                      }}
                    >
                      {translator.t(slide.secondary.labelKey)}
                    </button>
                  )}
                </div>
              </div>
            </article>
          ))}
        </div>
      )}

      {fasce.map((row, index) => (
        <section key={`${row.kind}-${index}`} style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {row.title !== null && (
            <h2 style={{ margin: 0, padding: '0 20px', font: `700 18px ${FONT_STACK}` }}>
              {row.title}
            </h2>
          )}
          <div
            style={{
              display: 'flex',
              gap: 10,
              overflowX: 'auto',
              scrollbarWidth: 'none',
              padding: '0 20px',
            }}
          >
            {tilesOf(row).map((tile) => (
              <button
                key={tile.id}
                type="button"
                onClick={() => onOpenRowItem(row, tile.id)}
                style={{
                  flex: 'none',
                  width: tile.wide ? 150 : 110,
                  border: 0,
                  background: 'none',
                  padding: 0,
                  cursor: 'pointer',
                  color: colors.text,
                  textAlign: 'left',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 6,
                }}
              >
                <span
                  aria-hidden
                  style={{
                    width: '100%',
                    // I poster sono verticali, le piastrelle dei canali e degli eventi orizzontali:
                    // usare una proporzione sola taglierebbe le locandine o gonfierebbe i loghi.
                    aspectRatio: tile.wide ? '16 / 9' : '2 / 3',
                    borderRadius: 10,
                    background: colors.surface,
                    backgroundImage: tile.image !== null ? `url(${tile.image})` : undefined,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                  }}
                />
                <span
                  style={{
                    font: `600 13px ${FONT_STACK}`,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {tile.label}
                </span>
                {tile.note !== null && (
                  <span style={{ font: `500 11px ${FONT_STACK}`, color: colors.accent, marginTop: -2 }}>
                    {tile.note}
                  </span>
                )}
              </button>
            ))}
          </div>
        </section>
      ))}
    </div>
  );
}

interface Tile {
  readonly id: string;
  readonly label: string;
  readonly image: string | null;
  /** Orizzontale: canali, aggregatori ed eventi. Verticale: le locandine di film e serie. */
  readonly wide: boolean;
  /** La riga in ambra sotto il titolo, quando c'è qualcosa da dire. */
  readonly note: string | null;
}

/**
 * Le piastrelle di una fascia, qualunque sia il suo tipo.
 *
 * Un `switch` esaustivo e non una serie di `if`: il tipo di `HomeRow` è una union discriminata, e
 * aggiungendone un caso senza trattarlo qui TypeScript se ne accorge. Una fascia nuova che
 * restituisse un elenco vuoto si vedrebbe come un titolo senza niente sotto — e si darebbe la colpa
 * al catalogo.
 */
function tilesOf(row: HomeRow): Tile[] {
  switch (row.kind) {
    case 'continue':
    case 'recs':
    case 'latest':
    case 'picks':
      return row.items.map((entry) => ({
        id: entry.item.id,
        label: entry.item.title,
        image: entry.item.posterUrl ?? null,
        wide: false,
        note: entry.because,
      }));
    case 'onair':
      return row.channels.map((entry) => ({
        id: entry.channel.id,
        label: entry.channel.name,
        image: entry.channel.logoUrl ?? null,
        wide: true,
        note: entry.now?.title ?? null,
      }));
    case 'aggs':
      return row.aggregators.map((entry) => ({
        id: entry.id,
        label: entry.label,
        image: entry.imageUrl,
        wide: true,
        note: null,
      }));
    case 'events':
      return row.events.map((entry) => ({
        id: entry.view.event.id,
        label: entry.view.event.title,
        image: null,
        wide: true,
        /*
          Il canale, quando **questa linea** ce l'ha.

          `channel` è `null` in un caso normale e frequente: gli eventi arrivano da una chiave sola
          per tutta l'installazione, e una linea può non possedere nessuno dei canali su cui vanno in
          onda. Scrivere lì il nome dell'emittente dallo scraper annuncerebbe una partita che quella
          linea non può guardare.
        */
        note: entry.channel?.name ?? null,
      }));
    case 'cats':
      /*
        Non arriva mai: `phoneHomeRows` l'ha già tolta. Il caso c'è perché il `switch` sia esaustivo —
        toglierlo renderebbe il tipo di ritorno `Tile[] | undefined` e costringerebbe a un ripiego
        proprio dove si vuole che il compilatore protesti.
      */
      return [];
  }
}
